// The search box, applied to the tree the server sent.
//
// It NEVER fetches — the whole tree is already in memory, which is the premise SearchBox
// is built on. What it does is re-prune, by the same rule the server prunes with: a node
// survives if it matches, if a file inside it matches, or if something below it does.
//
// MATCHING A NODE KEEPS EVERYTHING UNDER IT. Typing a client's name is asking for that
// client's documents, not for the ones whose filename happens to repeat the name — so a
// match at any level passes down and the subtree comes through whole. Only when nothing
// above matched does the filter descend to the files themselves.
//
// `fileCount` is RECOMPUTED on every node it rebuilds, never carried over. The count is
// what the fold header states, and a header claiming twelve documents over a list of two
// is the one thing a filtered tree must not do.
const norm = (value) => String(value || '').toLowerCase();

const hit = (value, q) => norm(value).includes(q);

// A file matches on either of its two names: the one a partner gave it and the one the
// file actually has. Both are on screen, so both are searchable — looking at a row and
// typing what you see must find it.
const fileHit = (file, q) => hit(file.title, q) || hit(file.original_name, q);

const countOf = (files, jobs) =>
  files.length + jobs.reduce((sum, job) => sum + job.fileCount, 0);

const filterJob = (job, q, inherited) => {
  if (inherited || hit(job.title, q)) return job;
  const files = job.files.filter((f) => fileHit(f, q));
  return files.length > 0 ? { ...job, files, fileCount: files.length } : null;
};

const filterRetainer = (retainer, q, inherited) => {
  if (inherited || hit(retainer.title, q)) return retainer;
  const files = retainer.files.filter((f) => fileHit(f, q));
  const jobs = retainer.jobs.map((j) => filterJob(j, q, false)).filter(Boolean);
  if (files.length === 0 && jobs.length === 0) return null;
  return { ...retainer, files, jobs, fileCount: countOf(files, jobs) };
};

const filterClient = (client, q) => {
  const all = hit(client.name, q);
  const retainers = client.retainers.map((r) => filterRetainer(r, q, all)).filter(Boolean);
  const jobs = client.jobs.map((j) => filterJob(j, q, all)).filter(Boolean);
  if (retainers.length === 0 && jobs.length === 0) return null;
  return {
    ...client,
    retainers,
    jobs,
    fileCount: retainers.reduce((sum, r) => sum + r.fileCount, 0) + countOf([], jobs),
  };
};

// An empty query returns the tree untouched — the same array, not a rebuilt copy, so
// nothing re-renders for a search nobody typed.
export default function filterTree(clients, query) {
  const q = norm(query).trim();
  if (!q) return clients;
  return clients.map((c) => filterClient(c, q)).filter(Boolean);
}
